import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import * as Sentry from "@sentry/react";

import { RequestError } from "../api/api-connector";
import { alert } from "../components/ui-kit/dialog-service";
import {
  networkErrorContent,
  requestErrorContent,
  accessDeniedContent,
} from "./error-dialog-content";

export function initGlobalErrorHandling() {
  window.addEventListener("error", (event) => {
    handleError(event.error).catch(logErrorHandlingFailure);
  });

  window.addEventListener("unhandledrejection", (event) => {
    handleError(event.reason).catch(logErrorHandlingFailure);
  });

  window.handleError = handleError;
}

export async function handleError(error: unknown) {
  if (isProbablyNetworkError(error)) {
    await alert(networkErrorContent(), i18n._(msg`Connection error`));
  } else if (error instanceof RequestError) {
    if (error.response?.status === 401) {
      // No need to show an alert, just log out
      return;
    }

    if (error.response?.status === 403) {
      await alert(accessDeniedContent(error), i18n._(msg`Access denied`));
    } else {
      await alert(requestErrorContent(error), i18n._(msg`Request error`));
    }
  } else {
    reportError(error);
  }
}

/**
 * Report an unexpected error to the console and error tracking, without
 * surfacing it to the user. Use for "should never happen" cases that we still
 * want visibility into.
 */
export function reportError(error: unknown) {
  console.error(error);
  Sentry.captureException(error);
}

function logErrorHandlingFailure(error: unknown) {
  console.error(error);
}

function isProbablyNetworkError(error: unknown) {
  return error instanceof TypeError && error.message === "Failed to fetch";
}
